// web/src/routes/supplements/Supplements.jsx
// Every supplement and medication, and which ones need ordering.
//
// Replaces a spreadsheet and the refill log on the feed room wall, so the test
// is not whether it holds the data — it is whether somebody in an aisle with
// gloves on would rather use this than a pen.
//
// The row leads with the number of unopened containers because that is the one
// fact a person standing in the feed room wants. Everything else on the row is
// why that number matters: how many owners, how long it lasts, whether anybody
// has to do something about it.
//
// Two states get said out loud rather than coloured in:
//
//   needs ordering   somebody's shelf hits empty inside the lead time.
//   can't predict    nobody has measured the container, or a horse has no
//                    dose. The row says which, because "unknown" with no
//                    reason is just a shrug.

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { fmtDateNear } from '../../lib/dates';
import { useAuth } from '../../lib/auth';
import { Panel, Empty, Rows, PlannerSkeleton } from '../../components/dashboard/Planner';

export default function SupplementsPage() {
  const { activeRole } = useAuth();
  const canManage = activeRole === 'owner' || activeRole === 'manager';

  const { data, isLoading } = useQuery({
    queryKey: ['supplements'],
    queryFn: () => api.get('/supplements'),
  });

  const items = data?.supplements ?? [];
  const active = items.filter((s) => s.active);
  const retired = items.filter((s) => !s.active);

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <header className="flex items-end justify-between gap-3 mb-4 pb-2 border-b-2 border-hunter/30">
        <div className="min-w-0">
          <h1 className="text-lg md:text-xl font-semibold leading-tight">Supplements</h1>
          <p className="text-sm text-ink-500">
            {active.length} {active.length === 1 ? 'product' : 'products'}
            {data?.needOrdering > 0 && (
              <>
                {' · '}
                <Link to="/supplements/orders" className="text-amber-700 font-medium hover:underline">
                  {data.needOrdering} to order
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 pb-1">
          <Link to="/supplements/history" className="text-sm text-hunter hover:underline">
            History
          </Link>
          {canManage && (
            <Link to="/supplements/new" className="btn-primary">
              Add
            </Link>
          )}
        </div>
      </header>

      {isLoading ? (
        <PlannerSkeleton />
      ) : items.length === 0 ? (
        <Panel title="Supplements">
          <Empty>
            Nothing set up yet.{' '}
            {canManage && (
              <Link to="/supplements/new" className="text-hunter hover:underline">
                Add the first one →
              </Link>
            )}
          </Empty>
        </Panel>
      ) : (
        <div className="space-y-4">
          {/* Setup before stock: a product nobody has measured cannot be
              predicted, and a row that quietly says "—" where a date should be
              is how that goes unnoticed for a month. */}
          {data?.needsSetup > 0 && canManage && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <strong>{data.needsSetup}</strong>{' '}
              {data.needsSetup === 1 ? 'product needs' : 'products need'} a container size or a
              daily dose before anything can be predicted for {data.needsSetup === 1 ? 'it' : 'them'}.
            </p>
          )}

          <Panel title="On the shelf" count={active.length || null}>
            {active.length === 0 ? (
              <Empty>Everything is retired.</Empty>
            ) : (
              <Rows>
                {active.map((s) => (
                  <li key={s.id}>
                    <SupplementRow item={s} today={data?.today} />
                  </li>
                ))}
              </Rows>
            )}
          </Panel>

          {retired.length > 0 && (
            <Panel title="Retired" count={retired.length}>
              <Rows>
                {retired.map((s) => (
                  <li key={s.id}>
                    <SupplementRow item={s} today={data?.today} />
                  </li>
                ))}
              </Rows>
            </Panel>
          )}
        </div>
      )}
    </div>
  );
}

function SupplementRow({ item, today }) {
  return (
    <Link to={`/supplements/${item.id}`} className="planner-row flex items-baseline gap-3">
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">
          {item.name}
          {/* Six of Westwood's nine are prescriptions. Saying so is not
              decoration: a dose on one of these is a clinical decision. */}
          {item.kind === 'medication' && (
            <span className="ml-1.5 align-middle text-[10px] font-semibold uppercase tracking-wider text-sky-700">
              Rx
            </span>
          )}
        </span>
        <span className="block text-xs text-ink-400 truncate">
          {[
            `${item.ownersOnIt} ${item.ownersOnIt === 1 ? 'owner' : 'owners'}`,
            `${item.horsesOnIt} ${item.horsesOnIt === 1 ? 'horse' : 'horses'}`,
            item.vendorName,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <Status item={item} today={today} />
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-lg font-semibold tabular-nums leading-none">{item.containers}</span>
        <span className="block text-[11px] text-ink-400">unopened</span>
      </span>
    </Link>
  );
}

/**
 * The one line that says whether anybody has to do something.
 *
 * Order matters: a product that needs ordering says so even if it also has a
 * setup gap, because one of those is urgent this week and the other has been
 * true since the import.
 */
function Status({ item, today }) {
  if (item.needOrdering > 0) {
    return (
      <span className="block text-xs text-amber-700 font-medium">
        {item.needOrdering} {item.needOrdering === 1 ? 'owner needs' : 'owners need'} more
        {item.firstEmptyOn && ` · first empty ${fmtDateNear(item.firstEmptyOn, today)}`}
      </span>
    );
  }
  if (item.arrivingOnAutoship > 0) {
    return (
      <span className="block text-xs text-sky-700">
        {item.arrivingOnAutoship} on autoship · nothing to order
      </span>
    );
  }
  if (item.needsYield || item.undosedHorses > 0) {
    // Name the missing thing. "Can't predict" on its own sends somebody
    // hunting through four screens for which number is absent.
    const missing = [
      item.needsYield && 'a container size',
      item.undosedHorses > 0 &&
        `a dose for ${item.undosedHorses} ${item.undosedHorses === 1 ? 'horse' : 'horses'}`,
    ].filter(Boolean);
    return <span className="block text-xs text-ink-400">Needs {missing.join(' and ')}</span>;
  }
  if (item.horsesOnIt === 0) {
    return <span className="block text-xs text-ink-400">Nobody is on it</span>;
  }
  if (item.firstEmptyOn) {
    const days = daysBetween(today, item.firstEmptyOn);
    return (
      <span className="block text-xs text-hunter">
        Enough until {fmtDateNear(item.firstEmptyOn, today)}
        {days != null && days >= 0 && ` · ${days} ${days === 1 ? 'day' : 'days'}`}
      </span>
    );
  }
  return null;
}

/** Whole days from one ISO date to another, or null if either is missing. */
function daysBetween(from, to) {
  if (!from || !to) return null;
  const a = new Date(from + 'T00:00:00Z').getTime();
  const b = new Date(to + 'T00:00:00Z').getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86400000);
}
